import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPendingApproval, approveTrade, rejectTrade } from '../api/api';
import ConfirmModal from '../components/ui/ConfirmModal';
import PageBackButton from '../components/PageBackButton';
import { toast } from 'sonner';
import { ArrowUpCircle, ArrowDownCircle, Bot, BrainCircuit, CheckCircle, XCircle, Loader2, Clock, Edit3, Timer } from 'lucide-react';
import { emitTradeApprovalChange } from '../lib/tradeApprovalEvents';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const getApprovalAction = (item) => item?.action || 'open_position';
const getActionLabel = (item) => item?.action_display || getApprovalAction(item).replace(/_/g, ' ');
const shortExecutionId = (id) => {
  if (!id) return 'N/A';
  const value = String(id);
  return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

function ExecutionLink({ id }) {
  if (!id) return 'N/A';
  return (
    <Link
      to={`/ai/executions/${id}`}
      className="text-primary hover:text-primary/80 underline underline-offset-2"
      title={String(id)}
    >
      {shortExecutionId(id)}
    </Link>
  );
}

export default function TradeApprovalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editable overrides
  const [lotSize, setLotSize] = useState('');
  const [slPrice, setSlPrice] = useState('');
  const [tpPrice, setTpPrice] = useState('');
  const [editing, setEditing] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchItem = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPendingApproval(id);
      setItem(data);
      setLotSize(data.validated_lot || '');
      setSlPrice(data.validated_sl || '');
      setTpPrice(data.validated_tp || '');
    } catch {
      toast.error('Failed to load trade approval');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      const overrides = {};
      if (editing && getApprovalAction(item) !== 'cancel_pending_order') {
        const action = getApprovalAction(item);
        const isCloseApproval = action === 'close_position';
        const isModifyApproval = action === 'modify_position';
        if (!isModifyApproval && lotSize && lotSize !== item.validated_lot) overrides.lot_size = lotSize;
        if (!isCloseApproval && slPrice && slPrice !== item.validated_sl) overrides.sl_price = slPrice;
        if (!isCloseApproval && tpPrice && tpPrice !== item.validated_tp) overrides.tp_price = tpPrice;
      }
      await approveTrade(id, overrides);
      toast.success('Approval executed');
      emitTradeApprovalChange();
      navigate('/approvals');
    } catch (e) {
      toast.error(e.message || 'Failed to approve trade');
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await rejectTrade(id, rejectReason);
      toast.success('Trade rejected');
      emitTradeApprovalChange();
      navigate('/approvals');
    } catch (e) {
      toast.error(e.message || 'Failed to reject trade');
    } finally {
      setBusy(false);
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center text-textSecondary">
        Trade approval not found.
      </div>
    );
  }

  const isPending = item.status === 'PENDING';
  const approvalAction = getApprovalAction(item);
  const approvalLabel = getActionLabel(item);
  const isCloseApproval = approvalAction === 'close_position';
  const isModifyApproval = approvalAction === 'modify_position';
  const isCancelApproval = approvalAction === 'cancel_pending_order';
  const showLotField = !isModifyApproval;
  const showProtectionFields = !isCloseApproval && !isCancelApproval;
  const parametersGridClass = showLotField && showProtectionFields
    ? 'grid-cols-3'
    : isCloseApproval || isCancelApproval
      ? 'grid-cols-1'
      : 'grid-cols-2';

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <PageBackButton label="Back to Approvals" fallbackTo="/approvals" hideLabelOnMobile />
        <h1 className="text-xl font-bold text-textPrimary">Trade Approval</h1>
        <span className="px-2.5 py-0.5 text-xs rounded-full bg-backgroundSurface border border-border text-textSecondary">
          {approvalLabel}
        </span>
        {isPending && (
          <span className="px-2.5 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Pending
          </span>
        )}
        {item.status === 'APPROVED' && (
          <span className="px-2.5 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">Approved</span>
        )}
        {item.status === 'REJECTED' && (
          <span className="px-2.5 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">Rejected</span>
        )}
        {item.status === 'EXPIRED' && (
          <span className="px-2.5 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-400">Expired</span>
        )}
      </div>

      {/* Trade info card */}
      <div className="bg-backgroundSurface border border-border rounded-lg p-5 space-y-4">
        {/* Symbol + direction + source */}
        <div className="flex items-center gap-3 flex-wrap">
          {item.source === 'AI' ? <BrainCircuit className="h-5 w-5 text-purple-400" /> : <Bot className="h-5 w-5 text-blue-400" />}
          <span className="text-lg font-bold text-textPrimary">{item.symbol}</span>
          {item.direction === 'BUY'
            ? <span className="inline-flex items-center gap-1 text-green-400 font-semibold"><ArrowUpCircle className="h-4 w-4" /> BUY</span>
            : <span className="inline-flex items-center gap-1 text-red-400 font-semibold"><ArrowDownCircle className="h-4 w-4" /> SELL</span>
          }
          <span className="text-xs text-textSecondary">{item.order_type}</span>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Field label="Source" value={item.source_display || item.source} />
          <Field label="Account" value={item.account_name || '—'} />
          <Field label="Risk %" value={item.risk_percent ? `${item.risk_percent}%` : '—'} />
          {item.ai_system_name && <Field label="AI System" value={item.ai_system_name} />}
          {item.ai_system_execution && <Field label="AI Execution" value={<ExecutionLink id={item.ai_system_execution} />} />}
          {item.bot_name && <Field label="Bot" value={item.bot_name} />}
          <Field label="Created" value={timeAgo(item.created_at)} />
          {isPending && item.expires_at && (() => {
            const ms = new Date(item.expires_at).getTime() - Date.now();
            if (ms <= 0) return <Field label="Expires" value="Expired" />;
            const m = Math.floor(ms / 60000);
            return <Field label="Expires" value={`${m}m remaining`} />;
          })()}
        </div>

        {item.reason && (
          <div className="text-sm">
            <span className="text-textSecondary">Reason:</span>{' '}
            <span className="text-textPrimary">{item.reason}</span>
          </div>
        )}

        {/* Editable parameters */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-textPrimary">
              {isCancelApproval ? 'Order to Cancel' : isCloseApproval ? 'Close Parameters' : isModifyApproval ? 'Protection Parameters' : 'Trade Parameters'}
            </span>
            {isPending && !isCancelApproval && (
              <button
                onClick={() => setEditing(!editing)}
                className="text-xs flex items-center gap-1 text-primary hover:text-primary/80 transition"
              >
                <Edit3 className="h-3 w-3" /> {editing ? 'Cancel edit' : 'Edit'}
              </button>
            )}
          </div>
          <div className={`grid gap-3 ${parametersGridClass}`}>
            {showLotField && (
              <ParamField
                label={isCloseApproval ? 'Close Volume' : 'Lot Size'}
                value={lotSize}
                editing={editing && isPending && !isCancelApproval}
                onChange={setLotSize}
              />
            )}
            {showProtectionFields && (
              <>
                <ParamField label="Stop Loss" value={slPrice} editing={editing && isPending} onChange={setSlPrice} />
                <ParamField label="Take Profit" value={tpPrice} editing={editing && isPending} onChange={setTpPrice} />
              </>
            )}
          </div>
        </div>

        {/* Override audit (if resolved) */}
        {!isPending && (item.override_lot || item.override_sl || item.override_tp) && (
          <div className="border-t border-border pt-3 text-xs text-textSecondary">
            <span className="font-medium">Overrides applied:</span>{' '}
            {item.override_lot && <span>Lot → {item.override_lot} </span>}
            {item.override_sl && <span>SL → {item.override_sl} </span>}
            {item.override_tp && <span>TP → {item.override_tp} </span>}
          </div>
        )}

        {/* Rejection details */}
        {item.status === 'REJECTED' && item.reject_reason && (
          <div className="border-t border-border pt-3 text-sm">
            <span className="text-textSecondary">Rejection reason:</span>{' '}
            <span className="text-red-400">{item.reject_reason}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {isPending && (
        <div className="flex gap-3">
          <button
            onClick={() => setConfirmAction('approve')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-600 hover:bg-green-700
                       text-white font-medium transition"
          >
            <CheckCircle className="h-4 w-4" /> Approve & Execute
          </button>
          <button
            onClick={() => setConfirmAction('reject')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-600 hover:bg-red-700
                       text-white font-medium transition"
          >
            <XCircle className="h-4 w-4" /> Reject
          </button>
        </div>
      )}

      {/* Confirm Approve */}
      <ConfirmModal
        open={confirmAction === 'approve'}
        title="Approve Trade"
        message={
          editing
            ? 'Execute this approval with the current form values?'
            : `Execute this ${approvalLabel.toLowerCase()} on the broker now?`
        }
        confirmLabel="Approve"
        onConfirm={handleApprove}
        onClose={() => !busy && setConfirmAction(null)}
        loading={busy}
      />

      {/* Confirm Reject */}
      <ConfirmModal
        open={confirmAction === 'reject'}
        title="Reject Trade"
        message={
          <div className="space-y-2">
            <p>Reject this trade? It will not be executed.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={2}
              className="w-full bg-backgroundSurface border border-border rounded px-3 py-2 text-sm text-textPrimary
                         placeholder:text-textSecondary resize-none"
            />
          </div>
        }
        confirmLabel="Reject"
        onConfirm={handleReject}
        onClose={() => !busy && setConfirmAction(null)}
        loading={busy}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-textSecondary">{label}</div>
      <div className="text-sm text-textPrimary font-medium">{value}</div>
    </div>
  );
}

function ParamField({ label, value, editing, onChange }) {
  return (
    <div>
      <div className="text-xs text-textSecondary mb-1">{label}</div>
      {editing ? (
        <input
          type="number"
          step="any"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full bg-background border border-border rounded px-2.5 py-1.5 text-sm text-textPrimary"
        />
      ) : (
        <div className="text-sm text-textPrimary font-medium">{value || '—'}</div>
      )}
    </div>
  );
}
